import { useState, useRef, useEffect } from "react";
import { MessageCircleQuestion, X, Send, Loader2, Bot, User, Sparkles } from "lucide-react";
import api from "../lib/api";

const SUGGESTIONS = [
  "Como creo mi primer menu?",
  "Como genero imagenes de platos?",
  "Que creditos consume cada funcion?",
  "Como activo mi pagina web?",
];

export default function HelpChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `s_${Date.now().toString(36)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    const userMsg = { role: "user", text: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await api.post("/help/chat", { message: msg, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", text: res.response, ts: Date.now() }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Lo siento, no pude procesar tu pregunta. Intentalo de nuevo.", ts: Date.now() }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="help-chatbot-btn"
          aria-label="Abrir asistente de ayuda"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg
                     flex items-center justify-center transition-transform hover:scale-110 active:scale-95" /* z-stack-doc: L5 help chatbot launcher */
          style={{ boxShadow: "0 4px 20px rgba(var(--primary), 0.35)" }}
        >
          <MessageCircleQuestion className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="help-chatbot-panel"
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)]
                     bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" /* z-stack-doc: L5 help chatbot panel */
          style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b bg-primary/5">
            <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
              <Bot className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold">Asistente MenuGenius</p>
              <p className="text-[10px] text-muted-foreground">Preguntame sobre la plataforma</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              data-testid="help-chatbot-close"
              aria-label="Cerrar asistente de ayuda"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Sparkles className="w-10 h-10 text-primary/20 mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">Hola! Soy tu asistente</p>
                <p className="text-xs text-muted-foreground mb-4">Preguntame cualquier cosa sobre MenuGenius</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      data-testid={`suggestion-${i}`}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border
                                 hover:border-primary/40 hover:bg-primary/5 transition-colors text-muted-foreground"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.ts} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap
                    ${msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted rounded-bl-sm"
                    }`}
                >
                  {msg.text}
                </div>
                {msg.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="bg-muted px-3 py-2 rounded-xl rounded-bl-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t p-3">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu pregunta..."
                data-testid="help-chatbot-input"
                className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30
                           placeholder:text-muted-foreground"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                data-testid="help-chatbot-send"
                className="w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center
                           disabled:opacity-40 transition-opacity hover:opacity-90"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
